import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { activateSnProductMapping } from '#/api/aftersalesMgmt/snProductMapping/activate';
import { deactivateSnProductMapping } from '#/api/aftersalesMgmt/snProductMapping/deactivate';
import { deleteSnProductMapping } from '#/api/aftersalesMgmt/snProductMapping/delete';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  getSnProductMappingApiErrorMessage,
  isSnProductMappingVersionConflict,
} from '#/modules/crmMgmt/aftersales/snProductMapping/helper/apiError';
import {
  canActivateSnProductMapping,
  canDeactivateSnProductMapping,
  canDeleteSnProductMapping,
} from '#/modules/crmMgmt/aftersales/snProductMapping/helper/snProductMappingStatus';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { toast } from '#/utils/toast';
import {
  snProductMappingDetailQueryKey,
  snProductMappingListQueryKeyPrefix,
} from '#/utils/queryKeys';

const SnProductMappingCommandBar = ({
  detail,
  onRefetch,
  onDeleted,
  showEdit = true,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const updatedAt = detail?.record?.updatedAt ?? null;

  const commandMutation = useMutation({
    mutationFn: async (action) => {
      if (action === 'activate') {
        return activateSnProductMapping(detail.id, { updatedAt });
      }
      if (action === 'deactivate') {
        return deactivateSnProductMapping(detail.id, { updatedAt });
      }
      return deleteSnProductMapping(detail.id);
    },
    retry: false,
    onSuccess: async (result, action) => {
      toast.success(result?.message || '操作成功');
      await queryClient.invalidateQueries({ queryKey: snProductMappingListQueryKeyPrefix() });
      if (action === 'delete') {
        onDeleted?.();
        navigate(ROUTES.snProductMapping);
        return;
      }
      await queryClient.invalidateQueries({
        queryKey: snProductMappingDetailQueryKey(detail.id),
      });
      await onRefetch?.();
    },
    onError: (error) => {
      toast.error(getSnProductMappingApiErrorMessage(error));
      if (isSnProductMappingVersionConflict(error)) {
        onRefetch?.();
      }
    },
  });

  async function handleActivate() {
    const ok = await confirm({
      title: '啟用 物料序號',
      message: '確定啟用這筆對照？啟用後不可再改客戶與產品代碼。',
      confirmText: '啟用',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate('activate');
  }

  async function handleDeactivate() {
    const ok = await confirm({
      title: '停用 物料序號',
      message: '確定停用這筆對照？停用後仍可查歷史，重新啟用時沿用原客戶。',
      confirmText: '停用',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate('deactivate');
  }

  async function handleDelete() {
    const ok = await confirm({
      title: '刪除 物料序號',
      message: '確定刪除這筆待指定的對照？刪除後無法復原。',
      confirmText: '刪除',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate('delete');
  }

  const isPending = commandMutation.isPending;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {showEdit ? (
          <Button type="button" variant="outline" asChild>
            <Link to={ROUTES.snProductMappingEdit(detail.id)}>編輯</Link>
          </Button>
        ) : null}
        {canDeleteSnProductMapping(detail.status) ? (
          <Button
            type="button"
            variant="destructive"
            disabled={isPending}
            onClick={handleDelete}
          >
            刪除
          </Button>
        ) : null}
        {canActivateSnProductMapping(detail) ? (
          <Button type="button" disabled={isPending} onClick={handleActivate}>
            啟用
          </Button>
        ) : null}
        {canDeactivateSnProductMapping(detail.status) ? (
          <Button
            type="button"
            variant="destructive"
            className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
            disabled={isPending}
            onClick={handleDeactivate}
          >
            停用
          </Button>
        ) : null}
      </div>
      {ConfirmDialogHost}
    </>
  );
};

SnProductMappingCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    customerId: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
  onDeleted: PropTypes.func,
  showEdit: PropTypes.bool,
};

export default SnProductMappingCommandBar;
